'use client'

import { usePathname } from 'next/navigation'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import Lenis from 'lenis'

interface LenisContextType {
  hasScrolled: boolean
  isMobile: boolean
  isXSScreen: boolean
  isTouchDevice: boolean
  isReducedMotion: boolean
  lenis: Lenis | undefined
  scrollPosition: { x: number; y: number }
  scrollDirection: 'up' | 'down' | null
  windowDimensions: { width: number; height: number }
}

const LenisContext = createContext<LenisContextType | undefined>(undefined)

export function useLenisContext() {
  const context = useContext(LenisContext)
  if (!context) {
    throw new Error('useLenisContext must be used within a LenisProvider')
  }
  return context
}

const getResponsiveLenisConfig = (prefersReducedMotion: boolean) => ({
  wheelMultiplier: prefersReducedMotion ? 0 : 1,
  lerp: prefersReducedMotion ? 0 : 0.08,
  autoRaf: true,
  // syncTouch: true,
  allowNestedScroll: true,
})

function isScrollStateInvalid(lenis: Lenis): boolean {
  const current = lenis.scroll
  const limit = lenis.limit
  return (
    current == null ||
    !Number.isFinite(current) ||
    current < 0 ||
    (Number.isFinite(limit) && current > limit)
  )
}

function LenisWrapper({ children }: { children: React.ReactNode }) {
  const currentPathname = usePathname()
  const lenisRef = useRef<Lenis | null>(null)
  const [lenis, setLenisInstance] = useState<Lenis | null>(null)

  const [isTouchDevice, setIsTouchDevice] = useState(false)
  const [isReducedMotion, setIsReducedMotion] = useState(false)

  const [windowDimensions, setWindowDimensions] = useState({
    width: 0,
    height: 0,
  })
  const [scrollPosition, setScrollPosition] = useState({ x: 0, y: 0 })
  const [hasScrolled, setHasScrolled] = useState(false)
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down' | null>(null)

  const isMobile = useMemo(
    () => windowDimensions.width > 0 && windowDimensions.width < 1024,
    [windowDimensions.width],
  )
  const isXSScreen = useMemo(() => windowDimensions.width < 360, [windowDimensions.width])

  const resetScrollState = useCallback(() => {
    setScrollPosition({ x: 0, y: 0 })
    setScrollDirection(null)
  }, [])

  const getDeviceSpecs = useCallback(() => {
    const isTouch = 'ontouchstart' in document.documentElement
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    setIsTouchDevice(isTouch)
    setIsReducedMotion(prefersReducedMotion)
  }, [])

  const initLenis = useCallback(() => {
    if (lenisRef.current) {
      lenisRef.current.destroy()
      lenisRef.current = null
    }
    const instance = new Lenis(getResponsiveLenisConfig(isReducedMotion))
    instance.start()
    lenisRef.current = instance
    setLenisInstance(instance)
    return instance
  }, [isReducedMotion])

  // Single Lenis instance: create on mount, destroy on unmount; reinit when isReducedMotion changes
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- the Lenis instance is an external system created here; state exposes it to context consumers
    initLenis()
    return () => {
      lenisRef.current?.destroy()
      lenisRef.current = null
      setLenisInstance(null)
    }
  }, [initLenis])

  // Reset scroll when path changes
  useEffect(() => {
    const instance = lenisRef.current
    if (instance) {
      instance.scrollTo(0, { immediate: true })
      resetScrollState()
    }
  }, [currentPathname, resetScrollState])

  const handleResize = useCallback(() => {
    const windowWidth = window.innerWidth
    const windowHeight = window.innerHeight

    document.documentElement.style.setProperty('--window-width', `${windowWidth}px`)
    document.documentElement.style.setProperty('--window-height', `${windowHeight}px`)

    setWindowDimensions({ width: windowWidth, height: windowHeight })

    getDeviceSpecs()

    lenisRef.current?.resize()
  }, [getDeviceSpecs])

  // Window dimensions and device specs can only be read after mount (SSR renders
  // with zeroed dimensions); reading them during render would cause hydration
  // mismatches, so this must stay an effect.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- see comment above
    handleResize()

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [handleResize])

  useEffect(() => {
    return () => {
      document.documentElement.classList.remove('scrollbar-hidden')
    }
  }, [])

  // When tab becomes visible again, resync or recreate Lenis if scroll state was corrupted by rAF throttling
  useEffect(() => {
    const onVisibilityChange = () => {
      const instance = lenisRef.current
      if (document.visibilityState !== 'visible' || !instance) return
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (isScrollStateInvalid(instance)) {
            initLenis()
          } else {
            instance.start()
          }
        })
      })
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [initLenis])

  const handleScroll = useCallback(
    ({ scroll, direction }: { scroll: number; direction: number }) => {
      const currentY = scroll
      const currentX = 0 // Lenis primarily handles vertical scrolling

      // Update scroll position
      setScrollPosition({ x: currentX, y: currentY })
      // Only reset at the top; otherwise stay true once scrolling has started
      setHasScrolled(currentY <= 5 ? false : true)

      // Determine scroll direction from Lenis direction value
      if (direction > 0) {
        setScrollDirection('down')
      } else if (direction < 0) {
        setScrollDirection('up')
      }
      // Note: We don't set direction to null when scroll stops, keeping the last direction
    },
    [],
  )

  // Scroll position and direction tracking
  useEffect(() => {
    const instance = lenisRef.current
    if (!instance) return

    let rafId: number | null = null
    let pendingUpdate = false

    const throttledHandleScroll = ({
      scroll,
      direction,
    }: {
      scroll: number
      direction: number
    }) => {
      if (!pendingUpdate) {
        pendingUpdate = true
        rafId = requestAnimationFrame(() => {
          handleScroll({ scroll, direction })
          pendingUpdate = false
        })
      }
    }

    // Listen to Lenis scroll events
    instance.on('scroll', throttledHandleScroll)

    // Set initial scroll position
    setScrollPosition({ x: 0, y: instance.scroll })

    return () => {
      instance.off('scroll', throttledHandleScroll)
      if (rafId) {
        cancelAnimationFrame(rafId)
      }
    }
  }, [handleScroll])

  // Handle scroll locking when dialogs are open
  useEffect(() => {
    const instance = lenisRef.current
    if (!instance) return

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-scroll-locked') {
          const body = document.body
          const isScrollLocked = body.getAttribute('data-scroll-locked') === '1'

          if (isScrollLocked) {
            instance.stop()
          } else {
            instance.start()
          }
        }
      })
    })

    // Observe body for data-scroll-locked attribute changes
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-scroll-locked'],
    })

    // Check initial state
    const isScrollLocked = document.body.getAttribute('data-scroll-locked') === '1'
    if (isScrollLocked) {
      instance.stop()
    }

    return () => {
      observer.disconnect()
    }
  }, [])

  const contextValue = useMemo(
    () => ({
      hasScrolled,
      lenis: lenis ?? undefined,
      isMobile,
      isXSScreen,
      isTouchDevice,
      isReducedMotion,
      scrollPosition,
      scrollDirection,
      windowDimensions,
    }),
    [
      lenis,
      windowDimensions,
      scrollPosition,
      scrollDirection,
      isMobile,
      isXSScreen,
      isTouchDevice,
      isReducedMotion,
      hasScrolled,
    ],
  )

  return <LenisContext.Provider value={contextValue}>{children}</LenisContext.Provider>
}

export function LenisProvider({ children }: { children: React.ReactNode }) {
  return <LenisWrapper>{children}</LenisWrapper>
}
